import React from 'react';
import { RefreshCw } from 'lucide-react';

export interface GlassButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  children?: React.ReactNode;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  style?: React.CSSProperties;
  title?: string;
  pill?: boolean;
  /** Accessible name / toggle state — passed straight through to the <button>. */
  'aria-label'?: string;
  'aria-pressed'?: boolean;
}

export const GlassButton: React.FC<GlassButtonProps> = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  children,
  className = '',
  type = 'button',
  icon,
  iconPosition = 'left',
  style = {},
  title,
  pill = false,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
}) => {
  const getVariantStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      fontWeight: 'var(--font-semibold)',
      cursor: disabled || loading ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'all var(--transition-normal)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--space-2)',
      whiteSpace: 'nowrap',
    };

    switch (variant) {
      case 'primary':
        return {
          ...baseStyles,
          borderRadius: '9999px', // Pill shape
          background: 'var(--primary-500)',
          color: '#ffffff',
          border: '1px solid var(--primary-500)',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1), 0 0 0 0 rgba(var(--primary-rgb, 59, 130, 246), 0)',
        };
      case 'secondary':
        return {
          ...baseStyles,
          borderRadius: '6px', // 6px radius as specified
          background: 'var(--panel)',
          color: 'var(--text)',
          border: '1px solid var(--stroke)',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        };
      case 'ghost':
        return {
          ...baseStyles,
          borderRadius: icon && !children ? '50%' : '6px', // Circle for icon-only, rounded for text
          background: 'transparent',
          color: 'var(--text-secondary)',
          border: '1px solid transparent',
        };
      case 'danger':
        return {
          ...baseStyles,
          borderRadius: '9999px', // Pill shape
          background: 'var(--danger)',
          color: '#ffffff',
          border: '1px solid var(--danger)',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        };
      default:
        return baseStyles;
    }
  };

  const getSizeStyles = (): React.CSSProperties => {
    const hasText = children !== undefined && children !== null;
    const isIconOnly = icon && !hasText;

    switch (size) {
      case 'sm':
        return {
          height: '32px', // 32px small height
          padding: isIconOnly ? '8px' : hasText && icon ? '8px 12px' : '8px 16px',
          fontSize: 'var(--text-sm)',
        };
      case 'lg':
        return {
          height: '40px', // 40px large height
          padding: isIconOnly ? '8px' : hasText && icon ? '8px 16px' : '8px 24px',
          fontSize: 'var(--text-base)',
        };
      case 'md':
      default:
        return {
          height: '36px', // 36px standard height
          padding: isIconOnly ? '8px' : hasText && icon ? '8px 12px' : '8px 16px',
          fontSize: 'var(--text-base)',
        };
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    
    const target = e.currentTarget;
    switch (variant) {
      case 'primary':
        target.style.background = 'var(--primary-600)';
        target.style.borderColor = 'var(--primary-600)';
        target.style.boxShadow = '0 0 20px rgba(var(--primary-rgb, 59, 130, 246), 0.4), 0 0 40px rgba(var(--primary-rgb, 59, 130, 246), 0.2)';
        break;
      case 'secondary':
        target.style.background = 'var(--panel-hover)';
        target.style.borderColor = 'var(--stroke-strong)';
        break;
      case 'ghost':
        target.style.background = 'var(--panel)';
        target.style.color = 'var(--text)';
        break;
      case 'danger':
        target.style.background = '#ef4444';
        target.style.borderColor = '#ef4444';
        target.style.boxShadow = '0 0 20px rgba(239, 68, 68, 0.4), 0 0 40px rgba(239, 68, 68, 0.2)';
        break;
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    
    const target = e.currentTarget;
    switch (variant) {
      case 'primary':
        target.style.background = 'var(--primary-500)';
        target.style.borderColor = 'var(--primary-500)';
        target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1), 0 0 0 0 rgba(var(--primary-rgb, 59, 130, 246), 0)';
        break;
      case 'secondary':
        target.style.background = 'var(--panel)';
        target.style.borderColor = 'var(--stroke)';
        break;
      case 'ghost':
        target.style.background = 'transparent';
        target.style.color = 'var(--text-secondary)';
        break;
      case 'danger':
        target.style.background = 'var(--danger)';
        target.style.borderColor = 'var(--danger)';
        target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
        break;
    }
  };

  const handleActive = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    const target = e.currentTarget;
    target.style.transform = 'scale(0.98)';
  };

  const handleInactive = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) return;
    const target = e.currentTarget;
    target.style.transform = 'scale(1)';
  };

  return (
    <button
      type={type}
      className={`glass-button ${className}`}
      style={{
        ...getVariantStyles(),
        ...getSizeStyles(),
        ...(pill ? { borderRadius: '9999px' } : {}),
        ...style,
      }}
      onClick={onClick}
      disabled={disabled || loading}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleActive}
      onMouseUp={handleInactive}
      title={title}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
    >
      {loading && (
        <span className="glass-button-spinner" style={{ 
          display: 'flex', 
          animation: 'spin 1s linear infinite',
          marginRight: children ? 'var(--space-2)' : '0',
        }}>
          <RefreshCw size={size === 'sm' ? 14 : 16} />
        </span>
      )}
      {icon && !loading && iconPosition === 'left' && (
        <span className="glass-button-icon" style={{ display: 'flex' }}>
          {icon}
        </span>
      )}
      {children}
      {icon && !loading && iconPosition === 'right' && (
        <span className="glass-button-icon" style={{ display: 'flex' }}>
          {icon}
        </span>
      )}
    </button>
  );
};

export default GlassButton;
